import { useState, useRef, useLayoutEffect } from 'react';
import { Icon } from 'semantic-ui-react';
import './SelectionActionBar.css';

// Breathing room kept between the bar and the panel edges.
const GUTTER = 16;

// Widest first — the first tier that fits wins. Labels are given up in order
// of how much they're needed: the count's wording, then the delete label (a red
// trash needs no caption), then the remaining labels.
const TIERS = ['full', 'medium', 'snug', 'compact'];

/**
 * The floating bar shown while a panel is in multi-select mode.
 *
 * Shared by the clothing grid and the lookbook panels so both read the same
 * way: how many things are selected, then the safe actions, then — set apart
 * behind a divider — the destructive one.
 *
 * @param {number} count            how many items are selected
 * @param {object[]} actions        [{ key, icon, label, onClick, disabled }]
 * @param {object} [destructive]    { icon, label, onClick } rendered last, in red
 */
const SelectionActionBar = ({ count = 0, actions = [], destructive }) => {
  const barRef = useRef(null);
  const contentRef = useRef(null);
  const [tier, setTier] = useState('full');

  // Degrade in stages rather than jumping straight to icons: first drop the
  // word "selected", then the action labels. The bar lives in a user-resizable
  // panel, so this measures the panel and real overflow, not the viewport.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const content = contentRef.current;
    const container = bar?.parentElement;
    if (!bar || !content || !container || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => {
      const available = container.clientWidth - GUTTER;
      const previous = bar.className;
      let chosen = TIERS[TIERS.length - 1];

      // Probe each tier before paint, so no intermediate state is visible.
      for (const candidate of TIERS) {
        bar.className = `selection-action-bar tier-${candidate}`;
        if (content.scrollWidth <= available) {
          chosen = candidate;
          break;
        }
      }

      bar.className = previous;
      setTier(chosen);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [count, actions.length, !!destructive]);

  if (count < 1) return null;

  return (
    <div ref={barRef} className={`selection-action-bar tier-${tier}`}>
      <div
        ref={contentRef}
        className="selection-action-bar-content"
        role="toolbar"
        aria-label="Selection actions"
      >
        <span className="selection-action-count">
          <span className="selection-action-count-num">{count}</span>
          <span className="selection-action-count-word">selected</span>
        </span>

        <span className="selection-action-divider" aria-hidden="true" />

        {actions.map(({ key, icon, label, onClick, disabled }) => (
          <button
            key={key}
            type="button"
            className="selection-action-btn"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
          >
            <Icon name={icon} />
            <span className="selection-action-label">{label}</span>
          </button>
        ))}

        {destructive && (
          <>
            <span className="selection-action-divider" aria-hidden="true" />
            <button
              type="button"
              className="selection-action-btn destructive"
              onClick={destructive.onClick}
              title={destructive.label}
              aria-label={`${destructive.label} ${count} selected`}
            >
              <Icon name={destructive.icon} />
              <span className="selection-action-label">{destructive.label}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SelectionActionBar;
